import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import { ROLE_LABELS, type Role } from '@/features/auth/api';
import { ApiError, apiFetch } from '@/lib/api/http';

interface InvitationPreview {
  email: string;
  role: Role;
  expires_at: string;
  requires_password: boolean;
  display_name: string | null;
}

/** 招待受諾画面（公開サインアップは無い / 指示書 §6-1）。 */
export function InvitationAcceptPage(): ReactElement {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('招待リンクが正しくありません。');
      return;
    }
    apiFetch<InvitationPreview>(
      `/invitations/accept/preview?token=${encodeURIComponent(token)}`,
    )
      .then((result) => {
        setPreview(result);
        // 招待の時点で入力された表示名があれば初期値にする（D-142）
        if (result.display_name) setDisplayName(result.display_name);
      })
      .catch((error: unknown) =>
        setLoadError(error instanceof ApiError ? error.message : '招待を確認できませんでした。'),
      );
  }, [token]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setSubmitError(null);
    try {
      await apiFetch<void>('/invitations/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: preview?.requires_password ? password : null,
          display_name: displayName || null,
        }),
      });
      setDone(true);
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.message : '受諾に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wpcanvas px-4">
      <div className="w-full max-w-md rounded-sm border border-wpborder bg-white px-5 py-6 shadow-sm">
        <h1 className="mb-4 text-lg font-semibold text-wptext-strong">招待の受諾</h1>

        {loadError && <ErrorState message={loadError} />}
        {!loadError && !preview && <SkeletonRows rows={3} columns={1} />}

        {preview && done && (
          <>
            <Notice kind="success" dismissible={false}>
              アカウントを作成しました。ログインしてください。
            </Notice>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="mt-2 w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover"
            >
              ログイン画面へ
            </button>
          </>
        )}

        {preview && !done && (
          <form onSubmit={(event) => void onSubmit(event)}>
            {submitError && (
              <Notice kind="error" dismissible={false}>
                {submitError}
              </Notice>
            )}
            <dl className="mb-4 grid grid-cols-[6rem_1fr] gap-y-1">
              <dt className="text-wptext-muted">メール</dt>
              <dd className="text-wptext-strong">{preview.email}</dd>
              <dt className="text-wptext-muted">ロール</dt>
              <dd className="text-wptext-strong">{ROLE_LABELS[preview.role]}</dd>
            </dl>

            <label htmlFor="accept-name" className="mb-1.5 block text-wptext-muted">
              表示名（任意）
            </label>
            <input
              id="accept-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className="mb-4 w-full rounded-sm border border-wpborder px-3 py-2"
            />

            {preview.requires_password && (
              <>
                <label htmlFor="accept-password" className="mb-1.5 block text-wptext-muted">
                  パスワード（10文字以上）
                </label>
                <input
                  id="accept-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={10}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mb-4 w-full rounded-sm border border-wpborder px-3 py-2"
                />
              </>
            )}

            {!preview.requires_password && (
              <p className="mb-4 text-wptext-muted">
                このロールは Google ログイン＋2要素認証を使うため、パスワードは設定しません。
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              {busy ? '処理中…' : '受諾してアカウントを作成'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
